import { apiFetch } from './http';
import { noteRefusedRead } from './newBuildAvailable';
import { type ClubList, settleListRead, startListRead } from './listReadStatus';
import { ScopeTransitionError } from './scopeTransition';

// ─────────────────────────────────────────────────────────────────────
// A CLUB LIST, READ IN PAGES (#1202) — the client half of
// apps/api/src/lib/legacyIdPage.ts.
//
// `GET /activities`, `/planned-matches` and `/single-matches` answer one page
// at a time and require `limit`, as `GET /bookings` has since #1196. This
// reads every page and hands back the whole list, so the callers — which have
// always wanted the club's whole list — are unchanged.
//
// The server pages in `legacyId` order (the order its index walks). The order
// the old single read used is restored here by the caller's `compare`, after
// the last page, with a STABLE sort so equal keys keep the page order.
//
// A failed page answers [] for the whole list, which is what each fetcher did
// when its single read failed. Since #1247 two things are added to that:
//   - A page the gateway refused (502/503/504 — an API restarting, or a
//     container swap) is asked for ONCE more, after a pause. Paging turned
//     one request into several, so a blip that one read would have missed
//     is now several times as likely to land on one of them.
//   - The outcome is recorded per list (`listReadStatus.ts`), so a failed
//     read is shown as one rather than as a club with nothing in it.
// ─────────────────────────────────────────────────────────────────────

/** Rows per request; the server's own ceiling is 2000 (`LIST_PAGE_MAX`). */
export const LIST_PAGE_SIZE = 1000;

/** A runaway guard: 500 pages of 1000 is far beyond any club. */
const MAX_PAGES = 500;

/** The gateway codes worth asking again: the API is coming back, not saying no. */
const RETRY_ON = new Set([502, 503, 504]);

/** The pause before the one retry — the first step of `saveWithRetry`'s backoff. */
export const PAGE_RETRY_DELAY_MS = 400;

/** One page, asked for at most twice. A read is safe to repeat. */
async function readPage(url: string): Promise<Response> {
  const res = await apiFetch(url);
  if (!RETRY_ON.has(res.status)) return res;
  await new Promise((resolve) => setTimeout(resolve, PAGE_RETRY_DELAY_MS));
  return apiFetch(url);
}

export async function fetchAllPages<T extends { id?: unknown }>(
  path: string,
  key: ClubList,
  compare: (a: T, b: T) => number,
): Promise<T[]> {
  const byId = new Map<unknown, T>();
  let after: string | null = null;
  const read = startListRead(key);
  try {
    for (let pageNo = 0; pageNo < MAX_PAGES; pageNo++) {
      const q = `limit=${LIST_PAGE_SIZE}` + (after ? `&after=${encodeURIComponent(after)}` : '');
      const res = await readPage(`${path}?${q}`);
      if (!res.ok) {
        // A stale bundle's bare read is refused 400 invalid_limit (#1244): say
        // so — and only so, since the stale-bundle notice already explains it.
        if (!(await noteRefusedRead(res))) settleListRead(read, false);
        return [];
      }
      const body = (await res.json()) as Record<string, unknown> | null;
      for (const row of (body?.[key] ?? []) as T[]) {
        byId.delete(row.id); // re-insert, so a later copy also takes the later place
        byId.set(row.id, row);
      }
      after = typeof body?.next === 'string' ? body.next : null;
      if (!after) {
        settleListRead(read, true);
        return [...byId.values()].sort(compare);
      }
    }
  } catch (err) {
    // No answer at all. Still thrown, as before; recorded first. A scope
    // switch in progress is not a failed read — nothing was asked.
    if (!(err instanceof ScopeTransitionError)) settleListRead(read, false);
    throw err;
  }
  settleListRead(read, false);
  return [];
}

/** A numeric field, ascending, with a missing value LAST — Postgres's `ASC`. */
export const ascNullsLast =
  <T>(field: keyof T) =>
  (a: T, b: T): number => {
    const x = a[field] == null ? null : Number(a[field]);
    const y = b[field] == null ? null : Number(b[field]);
    if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
    return x - y;
  };

/** A numeric field, descending, with a missing value FIRST — Postgres's `DESC`. */
export const descNullsFirst =
  <T>(field: keyof T) =>
  (a: T, b: T): number =>
    -ascNullsLast<T>(field)(a, b);
